import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  ActivatedRoute,
  Router,
  RouterOutlet,
  RouterLink,
} from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { COMMAND_PALETTE_SHORTCUT } from 'app/core/command-palette/command-palette.component';
import { CommandPaletteService } from 'app/core/command-palette/command-palette.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { AppSelectorComponent } from 'app/layout/common/app-selector/app-selector.component';
import { ResourceMeterComponent } from 'app/layout/common/resource-meter/resource-meter.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'compact-layout',
  templateUrl: './compact.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FuseLoadingBarComponent,
    MatButtonModule,
    MatIconModule,
    UserComponent,
    RouterOutlet,
    RouterLink,
    FuseVerticalNavigationComponent,
    AppSelectorComponent,
    ResourceMeterComponent,
  ],
})
export class CompactLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: boolean;
  /** Whether the navigation rail is currently open — drives the top-bar toggle button's visibility. */
  navigationOpened = true;
  navigation: Navigation;
  /**
   * Shortcut hint rendered on the quick-access button.
   *
   * Shared with the palette's own footer hint rather than sniffing the user
   * agent a second time — two copies of the same platform check are two things
   * that can disagree about which keyboard the user has.
   */
  readonly commandPaletteShortcut = COMMAND_PALETTE_SHORTCUT;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private _commandPaletteService = inject(CommandPaletteService);

  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _navigationService: NavigationService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation;
      });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
        // The rail is opened by default on desktop (side mode) and collapsed on
        // small screens (over mode). Mirror that here so the top-bar toggle only
        // appears when the rail is actually collapsed.
        this.navigationOpened = !this.isScreenSmall;
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Open the quick-access palette. The panel itself lives in the layout shell
   * (`<command-palette>`), so this only raises the shared open flag.
   */
  openCommandPalette(): void {
    this._commandPaletteService.open();
  }

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name,
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }
}
